import { useEffect, useState } from "react";
import { toast, ToastContainer } from 'react-toastify'; import { GetBatchPredictions, GetModalSummaryResult, handleFetchContribution, handleGenerateContribution } from "../../modules/predictorModule.ts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReusableTable from "../../components/common/Table.tsx";
import Button from "../../components/common/Button.tsx";
import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import SyncIcon from "@mui/icons-material/Sync";
import { Progress } from "reactstrap";
import { useSSE } from "../../context/SSEContext.tsx";

const ModalDetails = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [predictionLoading, setPredictionLoading] = useState(false)
    const [SummaryLoading, setSummaryLoading] = useState(false)
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isInprogress, setIsInprogess] = useState(false);
    const [recentPredictions, setRecentPredictions] = useState(false);
    const [modalSummary, setModalSummary] = useState(false);
    const [isGenerate, setIsGenerate] = useState(false)
    const navigate = useNavigate();
    //const toast = useToast();
    const location = useLocation();
    const { subscribeJob } = useSSE();

    useEffect(() => {
        if (!id) return;
        const jobs = JSON.parse(localStorage.getItem("contribution_jobs") || "{}");
        if (jobs[id] === "in_progress") {
            setIsInprogess(true);
        }
        fetchContributions(false);
    }, [id]);

    const predictor_details = location.state.predictor_details

    useEffect(() => {
        const getModalSummary = async () => {
            if (!id) {
                toast.error("Invalid train ID");
                return;
            }

            try {
                setSummaryLoading(true);
                const response = await GetModalSummaryResult(id);
                setModalSummary(response)
            } catch (error) {
                const errorMessage = error?.response?.data?.detail || "Failed to fetch recent batch prediction";
                toast.error(errorMessage);
            } finally {
                setSummaryLoading(false);
            }
        };
        getModalSummary()
    }, [])

    useEffect(() => {
        const getBatchPrediction = async () => {
            if (!id) {
                toast.error("Invalid train ID");
                return;
            }

            try {
                setPredictionLoading(true);
                const response = await GetBatchPredictions(id);
                setRecentPredictions(response.filter(pred => pred.prediction_type === "Form" || pred.prediction_type === "Forecast"));
            } catch (error) {
                const errorMessage = error?.response?.data?.detail || "Failed to fetch recent batch prediction";
                toast.error(errorMessage);
            } finally {
                setPredictionLoading(false);
            }
        };
        getBatchPrediction()
    }, [])

    const fetchContributions = async (isRefresh) => {
        if (!id) return;
        try {
            setTableLoading(true)
            const response = await handleFetchContribution(parseInt(id));
            if (response && response.data.status_code === 200) {
                const feature_importance = response.data.data.feature_importance
                const tableData = Object.entries(feature_importance).map(([key, value]) => ({
                    field: key,
                    importance: value.importance,
                    impact: value.impact,
                }));
                const columns = [
                    { header: 'Field Name', accessorKey: 'field' },
                    {
                        accessorKey: "importance",
                        header: "Contribution",
                        enableColumnFilter: false,
                        Cell: ({ row }) =>
                            <Progress max={100} value={row.original.importance} className="mt-2 w-100"></Progress>
                    },
                    {
                        accessorFn: (row) => row?.impact.replace(" Impact", ""),
                        header: "Impact"
                    },
                    {
                        header: 'Percentage',
                        accessorKey: 'importance',
                        Cell: ({ row }) => {
                            const importance = row.original.importance;

                            return (
                                <span
                                    className="fw-bold"
                                    style={{
                                        color: importance <= 0 ? "#a0a1a3" : importance <= 5 ? "#DC0C0A" : importance <= 10 ? "#F97614" : importance <= 20 ? "#facc15" : "#0CA347",
                                    }}
                                >
                                    {importance}%
                                </span>
                            );
                        },
                    }

                ];
                if (isRefresh)
                    setIsInprogess(false)
                if (response.data.status_code === 200 && predictor_details.is_incremental)
                    setIsGenerate(true)
                setColumns(columns)
                setData(tableData)
            } else if (response && response.data.status_code === 404) {
                setColumns([]);
                setData([]);
            } else {
                toast.error("Something went wrong while fetching data.");
            }

        } catch (error: any) {
            if (error.response?.status === 404) {
                setColumns([]);
                setData([]);
            } else {
                toast.error("Something went wrong while fetching data.");
            }
        } finally {
            setLoading(false);
            setTableLoading(false)
        }
    };

    useEffect(() => {
        fetchContributions(false);
    }, [id, toast]);

    const refreshContributions = async () => {
        await fetchContributions(true);
        if (data.length > 0 && id) {
            setIsInprogess(false);
            const jobs = JSON.parse(localStorage.getItem("contribution_jobs") || "{}");
            delete jobs[id];
            localStorage.setItem("contribution_jobs", JSON.stringify(jobs));
        }
    };

    const generateContribution = async () => {
        setIsDisabled(true);
        try {
            const response = await handleGenerateContribution(parseInt(id));
            if ("job_id" in response.data) {
                setIsInprogess(true);
                const jobs = JSON.parse(localStorage.getItem("contribution_jobs") || "{}");
                jobs[id] = "in_progress";
                localStorage.setItem("contribution_jobs", JSON.stringify(jobs));
                subscribeJob("shap", id);
            }
        } catch (error: any) {
            if (error.response?.status === 404) {
                setColumns([]);
                setData([]);
            } else {
                toast.error("Something went wrong while generating top fields contributing model training.");
            }
        } finally {
            setLoading(false);
            setIsDisabled(false);
            fetchContributions(false);
        }
    };

    const resultForPrediction = (train_id, prediction_id, predictor_name, problem_type) => {
        if (problem_type === "Forecast") {
            navigate(`/forecast-result/${train_id}`, {
                state: {
                    predictor_name: predictor_name,
                    prediction_id: prediction_id
                }
            })
        } else {
            navigate(`/form-prediction/${train_id}`, {
                state: {
                    prediction_id: prediction_id,
                    name: predictor_name,
                    problem_type: problem_type
                }
            })
        }
    }

    const displayName = (key) => {
        if (key === "use_case")
            return "Use Case"
        else if (key === "Algorithm")
            return "Algorithm Used"
        else if (key === "Training Date/Time")
            return "Training Date & Time"
        else
            return key;
    }
    const domainOptions = {
        "EnergyAnomalyDetection": "Energy Anomaly Detection",
        "CoolingSystemEfficiencyDetection": "Cooling System Efficiency Detection",
        "RANSiteFailurePrediction": "RAN Site Failure Prediction",
        "Churn": "Churn",
        "Forecast": "Forecast",
        "Network_Quality_of_Experience_QoE_Score_Prediction": "Network Quality of Experience (QoE) Score Prediction",
        "Network_Congestion_Prediction": "Network Congestion Prediction",
        "Signal_Strength_Estimation": "Signal Strength Estimator",
        "Packet_Loss_Rate_Prediction": "Packet loss rate prediction",
        "Latency_Estimation": "Latency estimation",
        "AnomalyDetection": "Anomaly Detection",
        "UsageForecast": "Usage Forecast",
        "EventVolumeForecast": "Event Volume Forecast",
    };

    const predictionsColumn = [
        { accessorFn: (row) => `${row?.date.split("T")[0] ?? "-"}`, header: "Date" },
        { accessorFn: (row) => `${domainOptions[row?.domain_name]}`, header: 'Domain Use Case' },
        { accessorFn: (row) => `${row?.problem_type === "UnsupervisedLearning" ? "Unsupervised Learning" : row.problem_type}`, header: 'Problem type' },
        {
            header: 'Accuracy',
            accessorKey: 'accuracy',
            Cell: ({ row }) => {
                const accuracy = row.original.accuracy;

                return (
                    <span
                        className="fw-bold"
                        style={{
                            color:
                                accuracy <= 49
                                    ? "#DC0C0A"
                                    : accuracy <= 75
                                        ? "#F97614"
                                        : "#0CA347",
                        }}
                    >
                        {accuracy}%
                    </span>

                );
            },
        },

        {
            // accessorKey: "dataset_usability",
            id: "Actions",
            enableColumnActions: false,
            // size:50,
            Cell: ({ row }) => {
                return (
                    <div className="ps-0 ms-0 me-0 pe-0">
                        <button
                            type="button"
                            onClick={() => resultForPrediction(row.original.train_id, row.original.prediction_id, row.original.predictor_name, row.original.problem_type)}
                            className="btn-label rv-primary-btn me-2 rounded-1 px-4 py-1 text-nowrap btn btn-secondary"
                            disabled={row.status === "Completed"}
                        >
                            <i className="ri-eye-line label-icon align-middle fs-16 me-1"></i>
                            <span className="ps-3 ms-2 me-1">Show results</span>
                        </button>
                    </div>
                );
            },
        },
    ]

    return (
        <>
            <BreadCrumb moduleName={"Training"} pageName={"View Predictor"} viewName={location?.state?.name} />
            <ToastContainer position="top-center" />
            {predictionLoading ? (
                <div className="flex justify-center items-center h-64">
                    <CircularProgress />
                </div>
            ) : <>
                <div className="d-flex flex-row gap-2">
                    <h5 className="rv-page-title p-2">Model Summary</h5>
                </div>
                <div className="border table-border p-3 mb-6">
                    <div className="summary-grid">
                        {Object.entries(modalSummary).map(([key, val]) => {
                            let displayVal = val;
                            if (key.toLowerCase().includes("date")) {
                                try {
                                    displayVal = new Date(val).toLocaleString("en-GB", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                        hour12: true,
                                    });
                                } catch (e) {
                                    console.error("Invalid date format:", val);
                                }
                            }

                            return (
                                <div key={key} className="summary-item">
                                    <div className="summary-key">{displayName(key) || key}</div>
                                    <div className="summary-val">{displayVal}</div>
                                </div>
                            );
                        })}

                    </div>
                </div>
            </>}
            <div className="d-flex flex-row gap-2">
                <h5 className="rv-page-title p-2">Top Fields Contributing to Model Training</h5>
                {(!isInprogress && data.length === 0) && (
                <div className="mt-3" style={{ marginLeft: "auto" }}>
                    <button
                        onClick={generateContribution}
                        disabled={isDisabled}
                        type="submit"
                        className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                    >
                        <i className="ri-bar-chart-2-fill label-icon align-middle fs-16 me-1 "></i>
                        <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                            Generate
                        </span>
                    </button>
                </div>
            )}
            </div>  
            
            {tableLoading ? (
                <div className="flex justify-center items-center h-64">
                    <CircularProgress />
                </div>
            ) : (
                <div className="container-fluid px-0">
                    <div className="disable-margin-card table-responsive table-customer border table-border w-100">
                        <ReusableTable
                            renderTopToolbarCustomActions={() => (
                                data.length !== 0 ? (
                                    <div style={{ marginLeft: "auto" }}>
                                        <Button onClick={generateContribution} disabled={!isGenerate} title={!isGenerate ? "Regeneration supported only for incremental models" : " "}>
                                            Generate
                                        </Button>
                                    </div>
                                ) : null
                            )}
                            columns={columns || []}
                            data={data || []}
                            enablePagination={true}
                            enableSorting={false}
                            enableColumnFilters={false}
                            renderEmptyRowsFallback={() => {
                                return (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            // alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            height: "100%",
                                            width: "100%",
                                            color: "GrayText",
                                            // margin: "25px 0px"
                                        }}
                                    >
                                        {(!isInprogress) ? <>
                                            <span className="font-medium">Click Generate to see the top contributing fields for model training</span>
                                            {/* <div className="mt-3" style={{ marginLeft: "auto" }}>
                                                <button
                                                    onClick={generateContribution}
                                                    disabled={isDisabled}
                                                    type="submit"
                                                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                                                >
                                                    <i className="ri-bar-chart-2-line label-icon align-middle fs-16 me-1 "></i>
                                                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                                        Generate
                                                    </span>
                                                </button>
                                            </div> */}
                                        </> : <>
                                            <span className="font-medium">Generation of the top contributing fields for model training is in progress...</span>
                                            {/* <div className="mt-3 mb-4" style={{ marginLeft: "auto" }}>
                                                <button
                                                    onClick={refreshContributions}
                                                    type="submit"
                                                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                                                >
                                                    <SyncIcon />
                                                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                                        Refresh
                                                    </span>
                                                </button>
                                            </div> */}
                                        </>}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>)}
            {(Array.isArray(location.state.datasource) ? location.state.datasource.includes("datalake") : location.state.datasource !== "datalake") && (
                <>
                    <div className="d-flex flex-row gap-2">
                        <h5 className="rv-page-title p-2">Recently Made Predictions</h5>
                    </div>
                    {predictionLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <CircularProgress />
                        </div>
                    ) : (
                        <div className="container-fluid px-0">
                            <div className="disable-margin-card table-responsive table-customer border table-border w-100">
                                <ReusableTable
                                    columns={predictionsColumn || []}
                                    data={recentPredictions || []}
                                    enablePagination={true}
                                    enableSorting={false}
                                    enableColumnFilters={false}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    )
}
export default ModalDetails;